import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import CreateLeagueForm from "./components/CreateLeagueForm";
import LandingHero from "./components/LandingHero";
import SeasonCalendarBanner from "./components/SeasonCalendarBanner";
import LoadingSpinner from "./components/LoadingSpinner";
import ErrorMessage from "./components/ErrorMessage";
import config from "./config";
import { devLog } from "./utils/devLog";
import { validateAllowedText } from "./utils/contentModeration";
import { isPublicRegistrationOpen, PUBLIC_REGISTRATION_CLOSED_MESSAGE, WEEK_1_SCORING_START_LABEL } from "./utils/seasonCalendar";
import leagueMembershipApi from "./services/leagueMembershipApi";

const OPEN_PUBLIC_LIST_MAX_HEIGHT = 400;

const QUICK_JOIN_DRAFT_HINT =
  "Joins an open league or creates a new one. New leagues get the next available Saturday or Sunday evening draft (Eastern), at least 48 hours out.";

function Footer({ onNavigate }) {
  const navigate = useNavigate();
  
  return (
    <footer
      style={{
        textAlign: "center",
        color: "#94a3b8",
        fontSize: "0.98em",
        marginTop: 40,
        padding: 20,
      }}
    >

      <div>
        <button
          onClick={() => onNavigate("about")}
          style={{
            color: "#2563eb",
            textDecoration: "underline",
            cursor: "pointer",
            fontSize: "0.9em",
            marginRight: 16,
            background: "none",
            border: "none"
          }}
        >
          About
        </button>
        <button
          onClick={() => navigate("/terms")}
          style={{
            color: "#2563eb",
            textDecoration: "underline",
            cursor: "pointer",
            fontSize: "0.9em",
            marginRight: 16,
            background: "none",
            border: "none"
          }}
        >
          Terms of Service
        </button>
        <button
          onClick={() => navigate("/privacy")}
          style={{
            color: "#2563eb",
            textDecoration: "underline",
            cursor: "pointer",
            fontSize: "0.9em",
            background: "none",
            border: "none"
          }}
        >
          Privacy Policy
        </button>
      </div>
    </footer>
  );
}

export default function LeagueSelection({ 
  currentUser,
  authResolved = false,
  onLogin, 
  onLogout, 
  onAdminLogin, 
  isAdminAuthenticated, 
  showToast,
  handlePaymentRequired,
  leaguesRefreshKey = 0,
  recentlyPaidLeagueId = null,
}) {
  const navigate = useNavigate();
  const publicLeaguesSectionRef = useRef(null);
  
  // League management state
  const [leagues, setLeagues] = useState([]);
  const [leaguesLoading, setLeaguesLoading] = useState(true);
  const [leaguesError, setLeaguesError] = useState(null);
  const [newLeagueName, setNewLeagueName] = useState("");
  const [creatingLeague, setCreatingLeague] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [openPublicLeagues, setOpenPublicLeagues] = useState([]);
  const [joiningPublic, setJoiningPublic] = useState(false);
  const [publicMembership, setPublicMembership] = useState(null);
  const [unpaidPrivateStatus, setUnpaidPrivateStatus] = useState(null);
  const [leavingLeagueId, setLeavingLeagueId] = useState(null);
  const publicRegistrationOpen = isPublicRegistrationOpen();
  const publicAtCap = Boolean(publicMembership?.at_cap);
  const unpaidPrivateAtCap = Boolean(unpaidPrivateStatus?.at_cap);
  const userPublicLeagues = leagues.filter((lg) => lg.is_public);
  const joinablePublicLeagues = openPublicLeagues.filter(
    (lg) => !leagues.some((userLg) => Number(userLg.id) === Number(lg.id))
  );
  const openPublicById = Object.fromEntries(
    openPublicLeagues.map((lg) => [Number(lg.id), lg])
  );

  // Fetch user's leagues
  const fetchLeagues = useCallback(async () => {
    if (!authResolved) return;
    setLeaguesLoading(true);
    setLeaguesError(null);
    try {
      if (!currentUser) {
        setLeagues([]);
        return;
      }
      devLog("Fetching leagues for user ID:", currentUser.id);
      const response = await fetch(`${config.API_URL}/leagues/?user_id=${currentUser.id}`, {
        credentials: "include"
      });
      if (!response.ok) throw new Error("Failed to fetch leagues");
      const data = await response.json();
      devLog("Leagues returned:", data);
      setLeagues(data);
    } catch (err) {
      console.error("Error fetching leagues:", err);
      setLeaguesError("Could not load leagues");
    } finally {
      setLeaguesLoading(false);
    }
  }, [authResolved, currentUser]);

  // Fetch open public leagues
  const fetchOpenPublicLeagues = useCallback(async () => {
    try {
      const res = await fetch(`${config.API_URL}/public-leagues/open`);
      if (!res.ok) throw new Error("Failed to fetch public leagues");
      const data = await res.json();
      setOpenPublicLeagues(data);
      setLeaguesError(null);
    } catch (err) {
      console.error("Error fetching public leagues:", err);
      setOpenPublicLeagues([]);
      setLeaguesError("Could not load public leagues. Please try again.");
    }
  }, []);

  const fetchPublicMembershipStatus = useCallback(async () => {
    if (!currentUser?.id) {
      setPublicMembership(null);
      return;
    }
    try {
      const res = await fetch(
        `${config.API_URL}/public-leagues/membership-status?user_id=${currentUser.id}`,
        { credentials: "include" }
      );
      if (res.ok) {
        setPublicMembership(await res.json());
      }
    } catch (err) {
      console.error("Error fetching public league membership status:", err);
    }
  }, [currentUser]);

  const fetchUnpaidPrivateStatus = useCallback(async () => {
    if (!currentUser?.id) {
      setUnpaidPrivateStatus(null);
      return;
    }
    try {
      const res = await fetch(
        `${config.API_URL}/leagues/unpaid-private-status?user_id=${currentUser.id}`,
        { credentials: "include" }
      );
      if (res.ok) {
        setUnpaidPrivateStatus(await res.json());
      }
    } catch (err) {
      console.error("Error fetching unpaid private league status:", err);
    }
  }, [currentUser]);

  // Handle league creation
  const handleCreateLeague = async (e) => {
    e.preventDefault();
    if (!newLeagueName.trim()) return;
    if (!currentUser) {
      setCreateError("Please log in to create a league");
      return;
    }

    try {
      validateAllowedText(newLeagueName, { fieldName: "League name", maxLength: 64 });
    } catch (err) {
      setCreateError(err.message);
      return;
    }
    
    setCreatingLeague(true);
    setCreateError(null);
    try {
      const res = await fetch(`${config.API_URL}/leagues/?user_id=${currentUser.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: newLeagueName,
          scoring_type: "STANDARD",
          draft_type: "SNAKE",
          salary_cap: 200,
          faab_budget: 100,
          use_faab: false,
          roster_unlock_day: "wednesday",
          team_count: 10
        })
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const detail = errData.detail;
        throw new Error(
          typeof detail === "string"
            ? detail
            : Array.isArray(detail)
              ? detail.map((d) => d.msg || d).join(", ")
              : "Failed to create league"
        );
      }
      const newLeague = await res.json();
      setNewLeagueName("");
      setCreatingLeague(false);
      
      // Refresh the leagues list to show the new league
      await fetchLeagues();
      await fetchUnpaidPrivateStatus();
      
      // For private leagues, require payment
      handlePaymentRequired("private_league", newLeague.id);
      
      showToast("League created! Complete payment to activate, then invite friends via League Settings → Manage Invites.", "success");
    } catch (err) {
      const message = err.message || "Could not create league. Try a different name.";
      setCreateError(message);
      setCreatingLeague(false);
      showToast(message, "error");
    }
  };

  // Handle quick join public league
  const quickJoinSuccessMessage = (payload) => {
    const name = payload?.league_name || "the league";
    switch (payload?.action) {
      case "created_and_joined":
        return `Created and joined ${name}. Public leagues are free.`;
      case "already_in_league":
        return `You're already in ${name}.`;
      case "joined":
      default:
        return `Joined ${name} successfully. Public leagues are free.`;
    }
  };

  const handleQuickJoinPublic = async () => {
    if (!currentUser) {
      onLogin();
      return;
    }
    setJoiningPublic(true);
    try {
      const res = await fetch(`${config.API_URL}/public-leagues/quick-join?user_id=${currentUser.id}`, {
        method: "POST",
        credentials: "include"
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to join public league");
      }
      const payload = await res.json();
      
      // Refresh both user's leagues and open public leagues to show the updated state
      await fetchLeagues();
      await fetchOpenPublicLeagues();
      await fetchPublicMembershipStatus();
      
      showToast(quickJoinSuccessMessage(payload), payload?.action === "already_in_league" ? "info" : "success");
    } catch (err) {
      console.error("Quick join error:", err);
      showToast(err.message || "Could not join public league. Please try again.", "error");
    }
    setJoiningPublic(false);
  };

  // Handle joining specific public league
  const handleJoinSpecificPublic = async (leagueId) => {
    if (!currentUser) {
      onLogin();
      return;
    }
    setJoiningPublic(true);
    try {
      const res = await fetch(`${config.API_URL}/leagues/${leagueId}/join?user_id=${currentUser.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({})
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to join public league");
      }
      
      // Refresh both user's leagues and open public leagues to show the updated state
      await fetchLeagues();
      await fetchOpenPublicLeagues();
      await fetchPublicMembershipStatus();
      
      // Public leagues are now FREE - no payment required
      showToast("League joined successfully! Public leagues are free.", "success");
    } catch (err) {
      console.error("Join specific public league error:", err);
      showToast(err.message || "Could not join public league. Please try again.", "error");
    }
    setJoiningPublic(false);
  };

  const handleLeavePublicLeague = async (league) => {
    if (!currentUser) return;
    const leagueName = league.name || `League ${league.id}`;
    const confirmed = window.confirm(
      `Leave "${leagueName}"?\n\nThis frees one of your public league slots (max ${publicMembership?.limit ?? 3}). You can join a different public league before its draft.`
    );
    if (!confirmed) return;

    setLeavingLeagueId(league.id);
    try {
      await leagueMembershipApi.leavePublicLeague(league.id, currentUser);
      await fetchLeagues();
      await fetchOpenPublicLeagues();
      await fetchPublicMembershipStatus();
      showToast(`Left ${leagueName}. Your public league slot is available again.`, "success");
    } catch (err) {
      console.error("Leave public league error:", err);
      showToast(err.message || "Could not leave public league.", "error");
    }
    setLeavingLeagueId(null);
  };

  // Handle selecting a league
  const handleSelectLeague = async (league) => {
    let paymentStatus = (league.payment_status || "unpaid").toLowerCase();

    if (!league.is_public && paymentStatus !== "paid") {
      // Refetch in case payment completed but the list is still stale
      if (currentUser?.id) {
        try {
          const response = await fetch(`${config.API_URL}/leagues/?user_id=${currentUser.id}`, {
            credentials: "include",
          });
          if (response.ok) {
            const data = await response.json();
            setLeagues(data);
            const fresh = data.find((lg) => Number(lg.id) === Number(league.id));
            if (fresh) {
              paymentStatus = (fresh.payment_status || "unpaid").toLowerCase();
            }
          }
        } catch (err) {
          console.error("Error refreshing league payment status:", err);
        }
      }
    }

    if (!league.is_public && paymentStatus !== "paid") {
      handlePaymentRequired("private_league", league.id);
      showToast("Complete payment to activate this private league.", "info");
      return;
    }
    navigate(`/league/${league.id}`);
  };

  useEffect(() => {
    if (!recentlyPaidLeagueId) return;
    setLeagues((prev) =>
      prev.map((lg) =>
        Number(lg.id) === Number(recentlyPaidLeagueId)
          ? { ...lg, payment_status: "paid" }
          : lg
      )
    );
    fetchUnpaidPrivateStatus();
  }, [recentlyPaidLeagueId, fetchUnpaidPrivateStatus]);

  useEffect(() => {
    if (authResolved) {
      fetchLeagues();
      fetchPublicMembershipStatus();
      fetchUnpaidPrivateStatus();
    }
  }, [authResolved, fetchLeagues, fetchPublicMembershipStatus, fetchUnpaidPrivateStatus, leaguesRefreshKey]);

  // Fetch public leagues when no league is selected
  useEffect(() => {
    fetchOpenPublicLeagues();
  }, [fetchOpenPublicLeagues]);

  const scrollToPublicLeagues = () => {
    publicLeaguesSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const isLoggedOut = authResolved && !currentUser;
  const isLoggedIn = authResolved && currentUser;

  return (
    <div className="ffb-bg">
      <header className="ffb-header">
        <h1 className="ffb-header-title ffb-header-title-long">🏈 Fantasy Football Classic™ 🏈</h1>
      </header>

      {isLoggedOut ? (
        <LandingHero
          onGetStarted={onLogin}
          onLearnMore={() => navigate("/about")}
          onBrowsePublic={scrollToPublicLeagues}
        />
      ) : null}

      {isLoggedIn ? (
        <div style={{ textAlign: "center", marginBottom: 12 }}>
          <button
            type="button"
            onClick={() => navigate("/about")}
            style={{
              background: "transparent",
              color: "#2563eb",
              border: "none",
              borderRadius: 6,
              padding: "4px 8px",
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 600,
              textDecoration: "underline",
              textUnderlineOffset: 3,
            }}
          >
            About Fantasy Football Classic™
          </button>
        </div>
      ) : null}

      <div className="ffb-card">
        {isLoggedIn ? (
          <div className="ffb-landing-user-bar">
            <span style={{ color: "#64748b", fontSize: 14 }}>
              Welcome, <strong>{currentUser.username}</strong>
            </span>
            <button
              type="button"
              onClick={onLogout}
              style={{
                background: "#dc2626",
                color: "white",
                border: "none",
                padding: "8px 16px",
                borderRadius: 4,
                cursor: "pointer",
                fontSize: 14,
              }}
            >
              Logout
            </button>
          </div>
        ) : null}

        <h1 className="ffb-main-heading">
          {!authResolved
            ? "Fantasy Football Classic™"
            : isLoggedIn
              ? "Select a League"
              : "Join the 2026 inaugural season"}
        </h1>
        <SeasonCalendarBanner />

        {/* Private Leagues Section — shown first so returning users reach their leagues quickly */}
        <div style={{ width: '100%', marginBottom: 24 }}>
          <h2 style={{ color: '#2563eb', fontSize: 20, marginBottom: 6 }}>Create or Join a Private League</h2>
          <div style={{ color: '#64748b', fontSize: 15, marginBottom: 10 }}>
            Private leagues are invite-only and managed by a commissioner (the creator). Create your own league and invite friends, or join a private league you&apos;ve been invited to. <strong>Members join free</strong> — only the commissioner pays a one-time <strong>$40 platform fee for the inaugural season</strong> when creating the league.
          </div>

          {leaguesLoading && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <LoadingSpinner text="Loading your leagues..." />
            </div>
          )}

          {leaguesError && (
            <ErrorMessage
              error={leaguesError}
              onRetry={fetchLeagues}
              title="Failed to load your leagues"
            />
          )}

          {!leaguesLoading && !leaguesError && (
            <ul style={{ listStyle: "none", padding: 0, width: "100%", marginBottom: 18 }}>
              {leagues.filter(lg => !lg.is_public).map(lg => (
                <li key={lg.id} style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 600, color: '#2563eb' }}>{lg.name}</span>
                  <button
                    style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontWeight: 600 }}
                    onClick={() => handleSelectLeague(lg)}
                    disabled={joiningPublic}
                  >
                    {joiningPublic ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <LoadingSpinner size="small" color="#fff" text="" />
                        Loading...
                      </span>
                    ) : (
                      "Go to League"
                    )}
                  </button>
                </li>
              ))}
              {leagues.filter(lg => !lg.is_public).length === 0 && (
                <li style={{ color: '#64748b', marginBottom: 12 }}>No private leagues found. Create one below!</li>
              )}
            </ul>
          )}

          <CreateLeagueForm
            newLeagueName={newLeagueName}
            setNewLeagueName={setNewLeagueName}
            creatingLeague={creatingLeague}
            createError={createError}
            onCreateLeague={handleCreateLeague}
            currentUser={currentUser}
            unpaidPrivateStatus={unpaidPrivateStatus}
            unpaidPrivateAtCap={unpaidPrivateAtCap}
          />
        </div>

        {/* Public Leagues Section */}
        <div ref={publicLeaguesSectionRef} id="public-leagues" style={{ width: '100%', marginBottom: 24 }}>
          <h2 className="ffb-section-heading" style={{ color: '#059669', marginBottom: 6 }}>Join a Public League</h2>
          <div className="ffb-body-text" style={{ color: '#64748b', marginBottom: 10 }}>
            Public leagues are open to everyone, have standard rules, and are managed by Fantasy Football Classic™. Use <strong style={{ color: '#059669' }}>Quick Join</strong> to enter instantly — <strong style={{ color: '#059669' }}>free, no payment required.</strong> Browse open leagues below when you want a specific draft time.
          </div>
          {currentUser && publicMembership && (
            <div style={{
              background: publicAtCap ? "#fef2f2" : "#f0fdf4",
              border: `1px solid ${publicAtCap ? "#fecaca" : "#bbf7d0"}`,
              borderRadius: 6,
              padding: 12,
              marginBottom: 16,
              fontSize: 14,
              color: publicAtCap ? "#991b1b" : "#166534",
              lineHeight: 1.5,
            }}>
              {publicAtCap ? (
                <>
                  You&apos;re in <strong>{publicMembership.count}</strong> of{" "}
                  <strong>{publicMembership.limit}</strong> allowed active public leagues (limit reached).
                  Private leagues are unlimited.
                </>
              ) : (
                <>
                  Public league membership: <strong>{publicMembership.count}</strong> of{" "}
                  <strong>{publicMembership.limit}</strong> active public leagues. Leave a league before its draft to free a slot. Private leagues are unlimited.
                </>
              )}
            </div>
          )}
          {currentUser && userPublicLeagues.length > 0 && (
            <div style={{ width: "100%", marginBottom: 16 }}>
              <h3 style={{ color: "#059669", fontSize: 17, marginBottom: 8 }}>Your public leagues</h3>
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {userPublicLeagues.map((lg) => {
                  const openMeta = openPublicById[Number(lg.id)];
                  return (
                  <li
                    key={lg.id}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      marginBottom: 10,
                      padding: "10px 12px",
                      background: "#f8fafc",
                      borderRadius: 8,
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 600, color: "#059669" }}>{lg.name}</span>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button
                          type="button"
                          style={{
                            background: "#2563eb",
                            color: "#fff",
                            border: "none",
                            borderRadius: 6,
                            padding: "6px 14px",
                            cursor: "pointer",
                            fontWeight: 600,
                          }}
                          onClick={() => handleSelectLeague(lg)}
                          disabled={Boolean(leavingLeagueId)}
                        >
                          Go to League
                        </button>
                        {lg.can_leave_public_league ? (
                          <button
                            type="button"
                            style={{
                              background: "#fff",
                              color: "#dc2626",
                              border: "1px solid #fecaca",
                              borderRadius: 6,
                              padding: "6px 14px",
                              cursor: "pointer",
                              fontWeight: 600,
                            }}
                            onClick={() => handleLeavePublicLeague(lg)}
                            disabled={leavingLeagueId === lg.id}
                          >
                            {leavingLeagueId === lg.id
                              ? "Leaving…"
                              : lg.public_league_draft_failed
                                ? "Leave (draft did not start)"
                                : "Leave League"}
                          </button>
                        ) : (
                          <span style={{ color: "#64748b", fontSize: 13, alignSelf: "center" }}>
                            Draft lobby open — can&apos;t leave
                          </span>
                        )}
                      </div>
                    </div>
                    {lg.public_league_draft_failed && (
                      <div style={{ fontSize: 13, color: "#b91c1c" }}>
                        This league&apos;s draft could not start. Leave to free your public league slot.
                      </div>
                    )}
                    {openMeta?.draft_time_display && (
                      <div style={{ fontSize: 13, color: "#64748b" }}>
                        Draft: {openMeta.draft_time_display}
                      </div>
                    )}
                  </li>
                  );
                })}
              </ul>
            </div>
          )}
          {publicRegistrationOpen ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
              <button
                className="ffb-btn"
                style={{ marginBottom: 8, fontWeight: 700, background: '#059669', color: '#fff' }}
                onClick={handleQuickJoinPublic}
                disabled={joiningPublic || leaguesLoading || publicAtCap}
                title={publicAtCap ? "Public league limit reached" : undefined}
              >
                {joiningPublic ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <LoadingSpinner size="small" color="#fff" text="" />
                    Joining...
                  </div>
                ) : (
                  "Quick Join Public League"
                )}
              </button>
              <div style={{ color: '#059669', fontSize: 14, fontWeight: 600, marginBottom: 8, textAlign: 'center' }}>
                Free to join — no payment required.
              </div>
              <div style={{ color: '#64748b', fontSize: 13, lineHeight: 1.5, marginBottom: 16, textAlign: 'center', maxWidth: 520 }}>
                {QUICK_JOIN_DRAFT_HINT}
              </div>
            </div>
          ) : (
            <div style={{
              background: '#fffbeb',
              border: '1px solid #f59e0b',
              borderRadius: 6,
              padding: 12,
              marginBottom: 16,
              fontSize: 14,
              color: '#92400e',
              lineHeight: 1.5,
            }}>
              {PUBLIC_REGISTRATION_CLOSED_MESSAGE}
            </div>
          )}
          
          {/* Loading state for public leagues */}
          {leaguesLoading && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <LoadingSpinner text="Loading public leagues..." />
            </div>
          )}
          
          {/* Error state for public leagues */}
          {leaguesError && (
            <ErrorMessage 
              error={leaguesError} 
              onRetry={fetchOpenPublicLeagues}
              title="Failed to load public leagues"
            />
          )}
          
          {!leaguesLoading && !leaguesError && joinablePublicLeagues.length > 0 && (
            <div style={{ width: "100%", marginBottom: 8 }}>
              <h3 style={{ color: "#059669", fontSize: 17, marginBottom: 8 }}>Open public leagues</h3>
              <div
                style={{
                  maxHeight: OPEN_PUBLIC_LIST_MAX_HEIGHT,
                  overflowY: "auto",
                  paddingRight: 4,
                  marginBottom: 8,
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  background: "#f8fafc",
                  padding: "8px 8px 0",
                }}
              >
                {joinablePublicLeagues.map(lg => (
                  <div key={lg.id} style={{
                    marginBottom: 16,
                    padding: 16,
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    background: '#fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, color: '#059669', fontSize: 16 }}>{lg.name}</span>
                        <span style={{
                          color: '#64748b',
                          fontSize: 14,
                          padding: '4px 8px',
                          background: '#f1f5f9',
                          borderRadius: 4
                        }}>
                          {lg.teams_joined_label || `${lg.teams_joined_count ?? lg.member_count} teams joined`}
                          {lg.open_spots > 0 ? ` · ${lg.open_spots} open` : ""}
                        </span>
                      </div>
                      <button
                        style={{
                          background: '#2563eb',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 6,
                          padding: '8px 16px',
                          cursor: 'pointer',
                          fontWeight: 600,
                          fontSize: 14
                        }}
                        onClick={() => handleJoinSpecificPublic(lg.id)}
                        disabled={joiningPublic || publicAtCap}
                        title={publicAtCap ? "Public league limit reached" : undefined}
                      >
                        {joiningPublic ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <LoadingSpinner size="small" color="#fff" text="" />
                            Joining...
                          </span>
                        ) : (
                          "Join League (Free)"
                        )}
                      </button>
                    </div>

                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 12px',
                      background: '#f0f9ff',
                      borderRadius: 6,
                      border: '1px solid #e0f2fe'
                    }}>
                      <span style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: '#0c4a6e',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4
                      }}>
                        🕐 Draft:
                      </span>
                      <span style={{
                        fontSize: 14,
                        color: '#0c4a6e',
                        fontWeight: 500
                      }}>
                        {lg.draft_time_display || "Not scheduled"}
                      </span>
                    </div>

                    <div style={{
                      display: 'flex',
                      gap: 16,
                      marginTop: 8,
                      fontSize: 12,
                      color: '#64748b',
                      flexWrap: 'wrap'
                    }}>
                      <span>Scoring: {lg.scoring_type}</span>
                      <span>Draft: {lg.draft_type}</span>
                      <span>{lg.open_spots} spots open</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {!leaguesLoading && !leaguesError && publicRegistrationOpen && (
            <div style={{ width: "100%", marginBottom: 18 }}>
              {openPublicLeagues.length === 0 && (
                <div style={{
                  color: '#334155',
                  marginBottom: 12,
                  textAlign: 'center',
                  padding: '20px',
                  background: '#f0fdf4',
                  borderRadius: 8,
                  border: '1px solid #bbf7d0',
                  lineHeight: 1.55,
                  fontSize: 14,
                }}>
                  <strong style={{ color: '#059669' }}>No leagues listed yet — use Quick Join above.</strong>
                  <br />
                  Joins an open league or creates a new public league for you. You do not need to wait for a league to appear in this list.
                </div>
              )}
              {openPublicLeagues.length > 0 && joinablePublicLeagues.length === 0 && userPublicLeagues.length === 0 && (
                <div style={{
                  color: '#334155',
                  marginBottom: 12,
                  textAlign: 'center',
                  padding: '20px',
                  background: '#f0fdf4',
                  borderRadius: 8,
                  border: '1px solid #bbf7d0',
                  lineHeight: 1.55,
                  fontSize: 14,
                }}>
                  <strong style={{ color: '#059669' }}>Every listed league is full.</strong>
                  <br />
                  Click <strong>Quick Join</strong> above — it will create a new public league and add you, with a draft on the next available weekend evening (Eastern).
                </div>
              )}
            </div>
          )}
        </div>

        {!authResolved ? (
          <div style={{ width: "100%", marginBottom: 24, textAlign: "center" }}>
            <LoadingSpinner text="Checking login..." />
          </div>
        ) : null}
        
      </div>
      {/* FAQ — short version; full details on About */}
      <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 2px 12px rgba(30,58,92,0.07)", padding: 28, maxWidth: 700, margin: "32px auto 0 auto" }}>
        <h2 style={{ color: "#2563eb", marginTop: 0, marginBottom: 12 }}>Quick answers</h2>
        <div style={{ color: "#334155", fontSize: 16, marginBottom: 10, fontWeight: 600 }}>How does scoring work?</div>
        <div style={{ color: "#64748b", marginBottom: 16, lineHeight: 1.55 }}>
          Each <strong>starter</strong> gets their own score from a <strong>real historical NFL game</strong> for that week number — only from seasons when that player actually played (1999–2025). Save your lineup to lock each starter&apos;s game; scores reveal at kickoff. Your <strong>full lineup total</strong> faces another team head-to-head; playoffs weeks 15–17.
        </div>
        <div style={{ color: "#334155", fontSize: 16, marginBottom: 10, fontWeight: 600 }}>How much does it cost?</div>
        <div style={{ color: "#64748b", marginBottom: 16, lineHeight: 1.55 }}>
          <strong style={{ color: "#059669" }}>Public leagues are free.</strong> Private leagues: commissioner pays a one-time platform fee ($40 inaugural season, 50% off). Members join private leagues free; any league dues between friends are outside the platform.
        </div>
        <div style={{ color: "#334155", fontSize: 16, marginBottom: 10, fontWeight: 600 }}>Can I play with friends?</div>
        <div style={{ color: "#64748b", marginBottom: 16, lineHeight: 1.55 }}>
          Yes — create a private league and invite friends, or use <strong>Quick Join</strong> for a free public league. Draft before Week 1 scoring ({WEEK_1_SCORING_START_LABEL}) for the full 14-week regular season.
        </div>
        <div className="ffb-landing-faq-more">
          <button type="button" className="ffb-landing-faq-link" onClick={() => navigate("/about")}>
            How it works, why it&apos;s different &amp; full FAQ →
          </button>
        </div>
      </div>
      <Footer onNavigate={(pageName) => navigate(`/${pageName}`)} />
      {/* Admin button - only visible to admin users */}
      {currentUser?.is_admin && (
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <button 
            onClick={onAdminLogin} 
            style={{ background: '#64748b', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', cursor: 'pointer', fontSize: 15 }}
          >
            {isAdminAuthenticated ? 'Admin Panel' : 'Admin Access'}
          </button>
          {isAdminAuthenticated && (
            <button 
              onClick={() => navigate('/admin')}
              style={{ 
                background: '#dc2626', 
                color: '#fff', 
                border: 'none', 
                borderRadius: 6, 
                padding: '8px 18px', 
                cursor: 'pointer', 
                fontSize: 15,
                marginLeft: 8
              }}
            >
              Logout Admin
            </button>
          )}
        </div>
      )}
    </div>
  );
}
